export type RtkPreloadPhase = 'eager' | 'inline' | 'pagination';

export type RtkPreloadCriticality = 'critical' | 'non-critical';

export type RtkPreloadCacheStatus = 'hit' | 'miss' | 'stale' | 'unknown';

export type RtkPreloadStatus =
  | 'started'
  | 'fulfilled'
  | 'cache-hit'
  | 'skipped'
  | 'degraded'
  | 'failed'
  | 'aborted';

export type RtkPreloadPaginationPolicy =
  | {
      mode: 'none';
      eagerPageLimit?: 0 | 1;
    }
  | {
      mode: 'cursor' | 'offset';
      eagerPageLimit: 1 | 2;
      pageSize: number;
      inlineContinuation: true;
      maxEagerPages?: 1 | 2;
    };

export interface RtkPreloadEndpointSelection {
  status?: string;
  data?: unknown;
  error?: unknown;
  isSuccess?: boolean;
  isLoading?: boolean;
  isFetching?: boolean;
  fulfilledTimeStamp?: number;
}

export interface RtkPreloadEndpoint<Args, State> {
  endpointId?: string;
  select: (args: Args) => (state: State) => RtkPreloadEndpointSelection;
  initiate: (
    args: Args,
    options?: {
      subscribe?: boolean;
      forceRefetch?: boolean;
    }
  ) => unknown;
}

export interface RtkPreloadRuntimeContext {
  routePath?: string;
  persona?: string | null;
  governance?: Record<string, unknown>;
  routeParams?: Record<string, string | undefined>;
  nowMs?: number;
}

export interface RtkPreloadOperationDescriptor<
  Context extends RtkPreloadRuntimeContext,
  Args,
  State,
> {
  moduleId: string;
  operationId: string;
  endpointId: string;
  phase: RtkPreloadPhase;
  endpoint: RtkPreloadEndpoint<Args, State>;
  buildArgs: (context: Context) => Args | null | undefined | Promise<Args | null | undefined>;
  pagination: RtkPreloadPaginationPolicy;
  criticality: RtkPreloadCriticality;
  timeoutMs?: number;
  dependsOn?: string[];
  mergeBehavior?: 'replace' | 'append' | 'upsert';
  enabled?: (context: Context) => boolean | Promise<boolean>;
  skipReason?: string | ((context: Context) => string);
  cachePolicy?: {
    maxAgeMs?: number;
    forceRefresh?: boolean;
    staleRefresh?: boolean;
    staleWhileRevalidate?: boolean;
  };
}

export interface RtkPreloadModuleDescriptor<
  Context extends RtkPreloadRuntimeContext,
  State,
> {
  moduleId: string;
  operations: ReadonlyArray<RtkPreloadOperationDescriptor<Context, any, State>>;
  enabled?: (context: Context) => boolean | Promise<boolean>;
  skipReason?: string | ((context: Context) => string);
}

export interface RtkPreloadTelemetryEvent {
  moduleId: string;
  operationId: string;
  endpointId: string;
  phase: RtkPreloadPhase;
  status: RtkPreloadStatus;
  cacheStatus: RtkPreloadCacheStatus;
  argsHash: string;
  pagination: RtkPreloadPaginationPolicy;
  durationMs: number;
  criticality: RtkPreloadCriticality;
  reason?: string;
  errorMessage?: string;
}

export interface RtkPreloadOperationResult {
  moduleId: string;
  operationId: string;
  endpointId: string;
  phase: RtkPreloadPhase;
  status: Exclude<RtkPreloadStatus, 'started'>;
  cacheStatus: RtkPreloadCacheStatus;
  argsHash: string;
  durationMs: number;
  reason?: string;
  error?: unknown;
}

export interface RtkPreloadRunnerOptions<
  Context extends RtkPreloadRuntimeContext,
  State,
> {
  modules?: ReadonlyArray<RtkPreloadModuleDescriptor<Context, State>>;
  operations?: ReadonlyArray<RtkPreloadOperationDescriptor<Context, any, State>>;
  dispatch: (action: unknown) => unknown;
  getState: () => State;
  concurrency?: number | ((context: Context, options: RtkPreloadRunOptions<Context>) => number);
  defaultTimeoutMs?: number;
  onTelemetry?: (event: RtkPreloadTelemetryEvent) => void;
  now?: () => number;
}

export interface RtkPreloadRunSignal {
  readonly aborted?: boolean;
}

export interface RtkPreloadRunOptions<Context extends RtkPreloadRuntimeContext> {
  context: Context;
  phases?: ReadonlyArray<RtkPreloadPhase>;
  moduleIds?: ReadonlyArray<string>;
  operationIds?: ReadonlyArray<string>;
  signal?: RtkPreloadRunSignal;
}

export interface PaginatedPreloadState<TItem, TCursor = string> {
  items: TItem[];
  nextToken?: TCursor | null;
  offset?: number;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  loadedPageCount: number;
  error?: string | null;
}

export interface MergePaginatedPreloadStateOptions<TItem, TCursor = string> {
  previous: PaginatedPreloadState<TItem, TCursor>;
  incoming: {
    items: TItem[];
    nextToken?: TCursor | null;
    offset?: number;
    hasNextPage?: boolean;
    error?: string | null;
  };
  selectKey: (item: TItem) => string;
  mode?: 'append' | 'replace' | 'upsert';
  isFetchingNextPage?: boolean;
}

type AnyOperation<Context extends RtkPreloadRuntimeContext, State> =
  RtkPreloadOperationDescriptor<Context, any, State>;

type AwaitableDispatchResult = {
  unwrap?: () => Promise<unknown>;
  abort?: () => void;
  unsubscribe?: () => void;
  error?: unknown;
};

const DEFAULT_TIMEOUT_MS = 12_000;

const stableStringify = (value: unknown): string => {
  const seen = new WeakSet<object>();
  const stringifyValue = (input: unknown): unknown => {
    if (!input || typeof input !== 'object') {
      return input;
    }
    if (seen.has(input)) {
      return '[Circular]';
    }
    seen.add(input);
    if (Array.isArray(input)) {
      return input.map((item) => stringifyValue(item));
    }
    const record = input as Record<string, unknown>;
    return Object.keys(record)
      .sort()
      .reduce<Record<string, unknown>>((next, key) => {
        next[key] = stringifyValue(record[key]);
        return next;
      }, {});
  };
  return JSON.stringify(stringifyValue(value));
};

export const createRtkPreloadArgsHash = (args: unknown): string => {
  const serialized = stableStringify(args);
  let hash = 5381;
  for (let index = 0; index < serialized.length; index += 1) {
    hash = ((hash << 5) + hash) ^ serialized.charCodeAt(index);
  }
  return `args_${(hash >>> 0).toString(16)}`;
};

export const defineRtkPreloadOperation = <
  Context extends RtkPreloadRuntimeContext,
  Args,
  State,
>(
  operation: RtkPreloadOperationDescriptor<Context, Args, State>
): RtkPreloadOperationDescriptor<Context, Args, State> => operation;

export const defineRtkPreloadModule = <
  Context extends RtkPreloadRuntimeContext,
  State,
>(
  module: RtkPreloadModuleDescriptor<Context, State>
): RtkPreloadModuleDescriptor<Context, State> => module;

export const mergePaginatedPreloadState = <TItem, TCursor = string>({
  previous,
  incoming,
  selectKey,
  mode = 'append',
  isFetchingNextPage = false,
}: MergePaginatedPreloadStateOptions<TItem, TCursor>): PaginatedPreloadState<TItem, TCursor> => {
  if (mode === 'replace') {
    return {
      items: [...incoming.items],
      nextToken: incoming.nextToken,
      offset: incoming.offset,
      hasNextPage: incoming.hasNextPage,
      isFetchingNextPage,
      loadedPageCount: 1,
      error: incoming.error ?? null,
    };
  }

  const byKey = new Map<string, TItem>();
  for (const item of previous.items) {
    byKey.set(selectKey(item), item);
  }
  for (const item of incoming.items) {
    const key = selectKey(item);
    if (mode === 'append' && byKey.has(key)) {
      continue;
    }
    byKey.set(key, item);
  }

  return {
    items: Array.from(byKey.values()),
    nextToken: incoming.nextToken,
    offset: incoming.offset,
    hasNextPage: incoming.hasNextPage,
    isFetchingNextPage,
    loadedPageCount: previous.loadedPageCount + 1,
    error: incoming.error ?? null,
  };
};

const resolveSkipReason = <Context extends RtkPreloadRuntimeContext>(
  reason: string | ((context: Context) => string) | undefined,
  context: Context,
  fallback: string
): string => {
  if (typeof reason === 'function') {
    return reason(context);
  }
  return reason ?? fallback;
};

const isFulfilledSelection = (selection: RtkPreloadEndpointSelection): boolean =>
  selection.isSuccess === true
  || selection.status === 'fulfilled'
  || selection.status === 'success'
  || selection.data !== undefined;

const cacheStatusFor = (
  selection: RtkPreloadEndpointSelection,
  maxAgeMs: number | undefined,
  nowMs: number
): RtkPreloadCacheStatus => {
  if (!isFulfilledSelection(selection)) {
    return 'miss';
  }
  if (!maxAgeMs || !selection.fulfilledTimeStamp) {
    return 'hit';
  }
  return nowMs - selection.fulfilledTimeStamp > maxAgeMs ? 'stale' : 'hit';
};

const isDispatchResult = (value: unknown): value is AwaitableDispatchResult =>
  Boolean(value && typeof value === 'object');

const isPromiseLike = <T>(value: T | Promise<T>): value is Promise<T> =>
  Boolean(value && typeof value === 'object' && 'then' in value && typeof value.then === 'function');

const withTimeout = async <T>(
  promise: Promise<T>,
  timeoutMs: number,
  onTimeout: () => void
): Promise<{ timedOut: boolean; value?: T }> => {
  const timers = globalThis as unknown as {
    setTimeout?: (callback: () => void, timeoutMs: number) => unknown;
    clearTimeout?: (timer: unknown) => void;
  };
  if (!timers.setTimeout || timeoutMs <= 0) {
    return { timedOut: false, value: await promise };
  }

  let timer: unknown;
  const timeoutPromise = new Promise<never>((_, reject) => {
    timer = timers.setTimeout?.(() => {
      onTimeout();
      reject(new Error(`RTK preload timed out after ${timeoutMs}ms`));
    }, timeoutMs);
  });

  try {
    const value = await Promise.race([promise, timeoutPromise]);
    return { timedOut: false, value };
  } catch (error) {
    if (error instanceof Error && error.message.includes('timed out')) {
      return { timedOut: true };
    }
    throw error;
  } finally {
    if (timer && timers.clearTimeout) {
      timers.clearTimeout(timer);
    }
  }
};

const awaitDispatchResult = async (result: unknown): Promise<unknown> => {
  if (isDispatchResult(result) && typeof result.unwrap === 'function') {
    return result.unwrap();
  }
  const resolved = await Promise.resolve(result);
  if (isDispatchResult(resolved) && resolved.error) {
    throw resolved.error;
  }
  return resolved;
};

const toErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error ?? 'Unknown RTK preload error');

export const createRtkPreloadRunner = <
  Context extends RtkPreloadRuntimeContext,
  State,
>({
  modules = [],
  operations = [],
  dispatch,
  getState,
  concurrency = 4,
  defaultTimeoutMs = DEFAULT_TIMEOUT_MS,
  onTelemetry,
  now = () => Date.now(),
}: RtkPreloadRunnerOptions<Context, State>) => {
  const flattenedOperations = (): AnyOperation<Context, State>[] => [
    ...operations,
    ...modules.flatMap((module) => module.operations),
  ];

  const emit = (
    operation: AnyOperation<Context, State>,
    status: RtkPreloadStatus,
    argsHash: string,
    cacheStatus: RtkPreloadCacheStatus,
    startedAt: number,
    extra: Pick<RtkPreloadTelemetryEvent, 'reason' | 'errorMessage'> = {}
  ) => {
    onTelemetry?.({
      moduleId: operation.moduleId,
      operationId: operation.operationId,
      endpointId: operation.endpointId,
      phase: operation.phase,
      status,
      cacheStatus,
      argsHash,
      pagination: operation.pagination,
      durationMs: Math.max(0, now() - startedAt),
      criticality: operation.criticality,
      ...extra,
    });
  };

  const runOperation = async (
    operation: AnyOperation<Context, State>,
    context: Context,
    signal: RtkPreloadRunSignal | undefined,
    cacheKeyRegistry: Set<string>
  ): Promise<RtkPreloadOperationResult> => {
    const startedAt = now();

    if (signal?.aborted) {
      emit(operation, 'aborted', 'args_aborted', 'unknown', startedAt, { reason: 'run-aborted' });
      return {
        moduleId: operation.moduleId,
        operationId: operation.operationId,
        endpointId: operation.endpointId,
        phase: operation.phase,
        status: 'aborted',
        cacheStatus: 'unknown',
        argsHash: 'args_aborted',
        durationMs: Math.max(0, now() - startedAt),
        reason: 'run-aborted',
      };
    }

    const isEnabled = operation.enabled ? await operation.enabled(context) : true;
    if (!isEnabled) {
      const reason = resolveSkipReason(operation.skipReason, context, 'operation-disabled');
      emit(operation, 'skipped', 'args_skipped', 'unknown', startedAt, { reason });
      return {
        moduleId: operation.moduleId,
        operationId: operation.operationId,
        endpointId: operation.endpointId,
        phase: operation.phase,
        status: 'skipped',
        cacheStatus: 'unknown',
        argsHash: 'args_skipped',
        durationMs: Math.max(0, now() - startedAt),
        reason,
      };
    }

    const args = await operation.buildArgs(context);
    if (args == null) {
      const reason = resolveSkipReason(operation.skipReason, context, 'args-unavailable');
      emit(operation, 'skipped', 'args_null', 'unknown', startedAt, { reason });
      return {
        moduleId: operation.moduleId,
        operationId: operation.operationId,
        endpointId: operation.endpointId,
        phase: operation.phase,
        status: 'skipped',
        cacheStatus: 'unknown',
        argsHash: 'args_null',
        durationMs: Math.max(0, now() - startedAt),
        reason,
      };
    }

    const argsHash = createRtkPreloadArgsHash(args);
    const dedupeKey = `${operation.endpointId}:${argsHash}`;
    if (cacheKeyRegistry.has(dedupeKey)) {
      emit(operation, 'skipped', argsHash, 'unknown', startedAt, { reason: 'duplicate-endpoint-args' });
      return {
        moduleId: operation.moduleId,
        operationId: operation.operationId,
        endpointId: operation.endpointId,
        phase: operation.phase,
        status: 'skipped',
        cacheStatus: 'unknown',
        argsHash,
        durationMs: Math.max(0, now() - startedAt),
        reason: 'duplicate-endpoint-args',
      };
    }
    cacheKeyRegistry.add(dedupeKey);

    const selection = operation.endpoint.select(args)(getState());
    const cacheStatus = cacheStatusFor(selection, operation.cachePolicy?.maxAgeMs, now());
    const shouldUseCache = cacheStatus === 'hit' && operation.cachePolicy?.forceRefresh !== true;
    if (shouldUseCache) {
      emit(operation, 'cache-hit', argsHash, cacheStatus, startedAt);
      return {
        moduleId: operation.moduleId,
        operationId: operation.operationId,
        endpointId: operation.endpointId,
        phase: operation.phase,
        status: 'cache-hit',
        cacheStatus,
        argsHash,
        durationMs: Math.max(0, now() - startedAt),
      };
    }

    if (
      cacheStatus === 'stale'
      && operation.cachePolicy?.staleRefresh === true
      && operation.cachePolicy.staleWhileRevalidate === true
    ) {
      dispatch(operation.endpoint.initiate(args, { subscribe: false, forceRefetch: true }));
      emit(operation, 'cache-hit', argsHash, cacheStatus, startedAt, { reason: 'stale-while-revalidate' });
      return {
        moduleId: operation.moduleId,
        operationId: operation.operationId,
        endpointId: operation.endpointId,
        phase: operation.phase,
        status: 'cache-hit',
        cacheStatus,
        argsHash,
        durationMs: Math.max(0, now() - startedAt),
        reason: 'stale-while-revalidate',
      };
    }

    emit(operation, 'started', argsHash, cacheStatus, startedAt);
    let request: unknown;
    try {
      request = dispatch(
        operation.endpoint.initiate(args, {
          subscribe: false,
          forceRefetch: cacheStatus === 'stale' || operation.cachePolicy?.forceRefresh === true,
        })
      );
      const timeoutResult = await withTimeout(
        awaitDispatchResult(request),
        operation.timeoutMs ?? defaultTimeoutMs,
        () => {
          if (isDispatchResult(request) && typeof request.abort === 'function') {
            request.abort();
          }
        }
      );
      if (timeoutResult.timedOut) {
        const status = operation.criticality === 'critical' ? 'failed' : 'degraded';
        emit(operation, status, argsHash, cacheStatus, startedAt, { reason: 'timeout' });
        return {
          moduleId: operation.moduleId,
          operationId: operation.operationId,
          endpointId: operation.endpointId,
          phase: operation.phase,
          status,
          cacheStatus,
          argsHash,
          durationMs: Math.max(0, now() - startedAt),
          reason: 'timeout',
        };
      }
      if (signal?.aborted) {
        if (isDispatchResult(request) && typeof request.abort === 'function') {
          request.abort();
        }
        emit(operation, 'aborted', argsHash, cacheStatus, startedAt, { reason: 'run-aborted' });
        return {
          moduleId: operation.moduleId,
          operationId: operation.operationId,
          endpointId: operation.endpointId,
          phase: operation.phase,
          status: 'aborted',
          cacheStatus,
          argsHash,
          durationMs: Math.max(0, now() - startedAt),
          reason: 'run-aborted',
        };
      }
      emit(operation, 'fulfilled', argsHash, cacheStatus, startedAt);
      return {
        moduleId: operation.moduleId,
        operationId: operation.operationId,
        endpointId: operation.endpointId,
        phase: operation.phase,
        status: 'fulfilled',
        cacheStatus,
        argsHash,
        durationMs: Math.max(0, now() - startedAt),
      };
    } catch (error) {
      const status = operation.criticality === 'critical' ? 'failed' : 'degraded';
      emit(operation, status, argsHash, cacheStatus, startedAt, {
        errorMessage: toErrorMessage(error),
      });
      return {
        moduleId: operation.moduleId,
        operationId: operation.operationId,
        endpointId: operation.endpointId,
        phase: operation.phase,
        status,
        cacheStatus,
        argsHash,
        durationMs: Math.max(0, now() - startedAt),
        error,
      };
    } finally {
      if (isDispatchResult(request) && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    }
  };

  const run = async ({
    context,
    phases,
    moduleIds,
    operationIds,
    signal,
  }: RtkPreloadRunOptions<Context>): Promise<RtkPreloadOperationResult[]> => {
    const phaseSet = phases ? new Set(phases) : null;
    const moduleSet = moduleIds ? new Set(moduleIds) : null;
    const operationSet = operationIds ? new Set(operationIds) : null;
    const moduleById = new Map(modules.map((module) => [module.moduleId, module]));

    const selected = flattenedOperations().filter((operation) => (
      (!phaseSet || phaseSet.has(operation.phase))
      && (!moduleSet || moduleSet.has(operation.moduleId))
      && (!operationSet || operationSet.has(operation.operationId))
    ));
    const operationOrder = new Map(selected.map((operation, index) => [operation.operationId, index]));

    const moduleSkipResults: RtkPreloadOperationResult[] = [];
    const runnable: AnyOperation<Context, State>[] = [];
    for (const operation of selected) {
      const module = moduleById.get(operation.moduleId);
      const moduleEnabled = module?.enabled ? await module.enabled(context) : true;
      if (!moduleEnabled) {
        const startedAt = now();
        const reason = resolveSkipReason(module?.skipReason, context, 'module-disabled');
        emit(operation, 'skipped', 'args_module_skipped', 'unknown', startedAt, { reason });
        moduleSkipResults.push({
          moduleId: operation.moduleId,
          operationId: operation.operationId,
          endpointId: operation.endpointId,
          phase: operation.phase,
          status: 'skipped',
          cacheStatus: 'unknown',
          argsHash: 'args_module_skipped',
          durationMs: Math.max(0, now() - startedAt),
          reason,
        });
      } else {
        runnable.push(operation);
      }
    }

    const results: RtkPreloadOperationResult[] = [...moduleSkipResults];
    const pending = new Map(runnable.map((operation) => [operation.operationId, operation]));
    const completed = new Map<string, RtkPreloadOperationResult>();
    const cacheKeyRegistry = new Set<string>();
    const resolvedConcurrency = typeof concurrency === 'function'
      ? concurrency(context, { context, phases, moduleIds, operationIds, signal })
      : concurrency;
    const effectiveConcurrency = Math.max(1, Math.floor(Number(resolvedConcurrency) || 1));
    const inFlight = new Map<string, Promise<void>>();

    const recordResult = (result: RtkPreloadOperationResult): void => {
      results.push(result);
      completed.set(result.operationId, result);
    };

    const launchOperation = (operation: AnyOperation<Context, State>): void => {
      pending.delete(operation.operationId);
      const task = runOperation(operation, context, signal, cacheKeyRegistry)
        .then(recordResult)
        .finally(() => {
          inFlight.delete(operation.operationId);
        });
      inFlight.set(operation.operationId, task);
    };

    const abortPendingOperations = (): void => {
      for (const operation of pending.values()) {
        const startedAt = now();
        emit(operation, 'aborted', 'args_aborted', 'unknown', startedAt, { reason: 'run-aborted' });
        recordResult({
          moduleId: operation.moduleId,
          operationId: operation.operationId,
          endpointId: operation.endpointId,
          phase: operation.phase,
          status: 'aborted',
          cacheStatus: 'unknown',
          argsHash: 'args_aborted',
          durationMs: Math.max(0, now() - startedAt),
          reason: 'run-aborted',
        });
      }
      pending.clear();
    };

    const skipDependencyBlockedOperations = (): void => {
      for (const operation of pending.values()) {
        const startedAt = now();
        emit(operation, 'skipped', 'args_dependency_blocked', 'unknown', startedAt, {
          reason: 'dependency-cycle-or-missing',
        });
        recordResult({
          moduleId: operation.moduleId,
          operationId: operation.operationId,
          endpointId: operation.endpointId,
          phase: operation.phase,
          status: 'skipped',
          cacheStatus: 'unknown',
          argsHash: 'args_dependency_blocked',
          durationMs: Math.max(0, now() - startedAt),
          reason: 'dependency-cycle-or-missing',
        });
      }
      pending.clear();
    };

    const findNextReadyOperation = (): AnyOperation<Context, State> | null => {
      for (const operation of pending.values()) {
        if (
          !operation.dependsOn?.length
          || operation.dependsOn.every((dependencyId) => completed.has(dependencyId))
        ) {
          return operation;
        }
      }
      return null;
    };

    while (pending.size > 0) {
      if (signal?.aborted) {
        abortPendingOperations();
        break;
      }

      let launched = false;
      while (inFlight.size < effectiveConcurrency) {
        const nextReady = findNextReadyOperation();
        if (!nextReady) {
          break;
        }
        launchOperation(nextReady);
        launched = true;
      }

      if (inFlight.size === 0) {
        if (!launched) {
          skipDependencyBlockedOperations();
        }
        break;
      }

      await Promise.race(inFlight.values());
    }

    if (signal?.aborted && pending.size > 0) {
      abortPendingOperations();
    }

    if (inFlight.size > 0) {
      await Promise.all(inFlight.values());
    }

    return results.sort((left, right) => (
      (operationOrder.get(left.operationId) ?? Number.MAX_SAFE_INTEGER)
      - (operationOrder.get(right.operationId) ?? Number.MAX_SAFE_INTEGER)
    ));
  };

  return {
    run,
  };
};

// ============================================================================
// Consumer Primitives (HOC-style combined loading lifecycle)
// These enable route containers / pages to declare "I own these preloaded
// operations" and derive a unified ready state that suppresses local loading
// UIs when the cache is warm from the preload runner.
// ============================================================================

export type RtkPreloadedOperationReadyState = {
  isReady: boolean;
  isFetching: boolean;
  hasData: boolean;
  fulfilledTimeStamp: number | null;
  argsHash: string | null;
  cacheStatus: RtkPreloadCacheStatus;
  status: 'idle' | 'loading' | 'ready' | 'stale' | 'error';
};

export type RtkPreloadedModuleReadyState = {
  isModuleReady: boolean; // all critical ops for the module are ready (or skipped)
  criticalReadyCount: number;
  criticalTotal: number;
  isAnyFetching: boolean;
  lastFulfilledAt: number | null;
  operations: Record<string, RtkPreloadedOperationReadyState>;
};

export type CreateRtkPreloadedOperationSelectorOptions = {
  maxAgeMs?: number;
  treatStaleAsReady?: boolean;
};

/**
 * Creates a memoizable selector that, given the exact operation descriptor
 * the preload runner uses, returns a combined ready state for that single
 * operation. The caller (app) is responsible for passing the same args
 * the preload used (or a builder that reproduces them).
 */
export const createRtkPreloadedOperationSelector = <
  Context extends RtkPreloadRuntimeContext,
  Args,
  State,
>(
  operation: RtkPreloadOperationDescriptor<Context, Args, State>,
  getEndpoint: () => RtkPreloadEndpoint<Args, State>,
  options: CreateRtkPreloadedOperationSelectorOptions = {}
): ((state: State, args: Args | null | undefined) => RtkPreloadedOperationReadyState) => {
  const { maxAgeMs, treatStaleAsReady = false } = options;

  return (state: State, args: Args | null | undefined) => {
    if (args == null) {
      return {
        isReady: false,
        isFetching: false,
        hasData: false,
        fulfilledTimeStamp: null,
        argsHash: null,
        cacheStatus: 'unknown',
        status: 'idle',
      };
    }

    const endpoint = getEndpoint();
    const selection = endpoint.select(args)(state);
    const now = Date.now();
    const cacheStatus = cacheStatusFor(selection, maxAgeMs ?? operation.cachePolicy?.maxAgeMs, now);
    const hasData = isFulfilledSelection(selection);
    const isFetching = selection.isFetching === true || selection.isLoading === true;
    const fulfilledTimeStamp =
      typeof selection.fulfilledTimeStamp === 'number' ? selection.fulfilledTimeStamp : null;
    const argsHash = createRtkPreloadArgsHash(args);

    let status: RtkPreloadedOperationReadyState['status'] = 'idle';
    let isReady = false;

    if (hasData) {
      const isStale = cacheStatus === 'stale';
      isReady = !isFetching && (!isStale || treatStaleAsReady);
      status = isFetching ? 'loading' : isStale ? 'stale' : 'ready';
    } else if (isFetching) {
      status = 'loading';
    }

    return {
      isReady,
      isFetching,
      hasData,
      fulfilledTimeStamp,
      argsHash,
      cacheStatus,
      status,
    };
  };
};

/**
 * Creates a selector for an entire module's critical operations.
 * The app passes the list of critical operation descriptors that were
 * declared for that module in the preload contract.
 */
export const createRtkPreloadedModuleSelector = <
  Context extends RtkPreloadRuntimeContext,
  State,
>(
  _moduleId: string,
  criticalOperations: ReadonlyArray<RtkPreloadOperationDescriptor<Context, any, State>>,
  getEndpointFor: (op: RtkPreloadOperationDescriptor<Context, any, State>) => RtkPreloadEndpoint<any, State>,
  options?: CreateRtkPreloadedOperationSelectorOptions
): ((state: State, context: Context) => RtkPreloadedModuleReadyState) => {
  const opSelectors = criticalOperations.map((op) =>
    createRtkPreloadedOperationSelector(op, () => getEndpointFor(op), options)
  );

  return (state: State, context: Context) => {
    const operations: Record<string, RtkPreloadedOperationReadyState> = {};
    let criticalReadyCount = 0;
    let isAnyFetching = false;
    let lastFulfilledAt: number | null = null;

    criticalOperations.forEach((op, index) => {
      const builtArgs = op.buildArgs(context);
      const args = isPromiseLike(builtArgs) ? null : builtArgs;
      const sel = opSelectors[index](state, args);
      operations[op.operationId] = sel;

      if (sel.isReady) criticalReadyCount += 1;
      if (sel.isFetching) isAnyFetching = true;
      if (sel.fulfilledTimeStamp && (!lastFulfilledAt || sel.fulfilledTimeStamp > lastFulfilledAt)) {
        lastFulfilledAt = sel.fulfilledTimeStamp;
      }
    });

    const isModuleReady =
      criticalOperations.length === 0 || criticalReadyCount === criticalOperations.length;

    return {
      isModuleReady,
      criticalReadyCount,
      criticalTotal: criticalOperations.length,
      isAnyFetching,
      lastFulfilledAt,
      operations,
    };
  };
};

/**
 * Pure predicate: given the ready state from the selector above, should the
 * calling route/container render its primary content or a loading placeholder?
 */
export const shouldRenderPreloadedContent = (ready: RtkPreloadedOperationReadyState | RtkPreloadedModuleReadyState): boolean => {
  if ('isModuleReady' in ready) {
    return ready.isModuleReady && !ready.isAnyFetching;
  }
  return ready.isReady;
};
